// Custom Cursor Functionality
class CustomCursor {
  constructor() {
    this.cursor = null;
    this.isEnabled = false;
    this.cursorSize = 32;
    this.init();
  }

  init() {
    // Check if device is mobile/touch
    const isMobile = () => {
      return (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        ) ||
        window.innerWidth <= 768 ||
        "ontouchstart" in window ||
        navigator.maxTouchPoints > 0
      );
    };

    // If mobile device, don't initialize custom cursor
    if (isMobile()) {
      return;
    }

    // Check if custom cursor is enabled via shopSettings
    if (typeof window.shopSettings !== "undefined") {
      this.isEnabled = window.shopSettings.customCursorEnabled;
      this.cursorSize = window.shopSettings.customCursorSize || 32;
    } else {
      // Fallback: check for data attributes on body
      const body = document.body;
      this.isEnabled =
        body.getAttribute("data-custom-cursor-enabled") === "true";
      this.cursorSize =
        parseInt(body.getAttribute("data-custom-cursor-size")) || 32;
      const cursorImage = body.getAttribute("data-custom-cursor-image");

      if (cursorImage) {
        this.cursorImage = cursorImage;
      }
    }

    // If not enabled, don't proceed
    if (!this.isEnabled) {
      return;
    }

    // Add the custom cursor element to the DOM
    this.createCursorElement();

    // Set up event listeners
    this.setupEventListeners();

    // Add the enabled class to body
    document.body.classList.add("custom-cursor-enabled");
  }

  createCursorElement() {
    // Create cursor container
    this.cursor = document.createElement("div");
    this.cursor.className = "custom-cursor";
    this.cursor.style.setProperty("--cursor-size", `${this.cursorSize}px`);

    // Get cursor image from shopSettings or fallback
    const cursorImageSrc =
      (window.shopSettings && window.shopSettings.customCursorImage) ||
      this.cursorImage;

    if (cursorImageSrc) {
      const cursorImage = document.createElement("img");
      cursorImage.src = cursorImageSrc;
      cursorImage.alt = "Custom cursor";
      this.cursor.appendChild(cursorImage);
    } else {
      // Fallback to a simple colored circle if no image is set
      this.cursor.style.backgroundColor = "#000";
      this.cursor.style.borderRadius = "50%";
      this.cursor.style.border = "2px solid #fff";
    }

    document.body.appendChild(this.cursor);
  }

  setupEventListeners() {
    // Track mouse movement
    document.addEventListener("mousemove", (e) => {
      if (this.cursor) {
        this.cursor.style.left = e.clientX + "px";
        this.cursor.style.top = e.clientY + "px";
      }
    });

    // Handle cursor visibility on mouse enter/leave
    document.addEventListener("mouseenter", () => {
      if (this.cursor) {
        this.cursor.style.display = "block";
      }
    });

    document.addEventListener("mouseleave", () => {
      if (this.cursor) {
        this.cursor.style.display = "none";
      }
    });

    // Handle form elements - show default cursor
    const formElements = document.querySelectorAll("input, textarea, select");
    formElements.forEach((element) => {
      element.addEventListener("focus", () => {
        if (this.cursor) {
          this.cursor.style.display = "none";
        }
      });

      element.addEventListener("blur", () => {
        if (this.cursor) {
          this.cursor.style.display = "block";
        }
      });
    });

    // Handle disabled elements
    const disabledElements = document.querySelectorAll(
      "button[disabled], .disabled"
    );
    disabledElements.forEach((element) => {
      element.addEventListener("mouseenter", () => {
        if (this.cursor) {
          this.cursor.style.display = "none";
        }
      });

      element.addEventListener("mouseleave", () => {
        if (this.cursor) {
          this.cursor.style.display = "block";
        }
      });
    });
  }

  destroy() {
    if (this.cursor) {
      this.cursor.remove();
      this.cursor = null;
    }
    document.body.classList.remove("custom-cursor-enabled");
  }
}

// Initialize custom cursor when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  // Wait for shopSettings to be available with timeout
  let attempts = 0;
  const maxAttempts = 20; // 1 second timeout

  const waitForSettings = () => {
    attempts++;
    if (typeof window.shopSettings !== "undefined") {
      new CustomCursor();
    } else if (attempts < maxAttempts) {
      setTimeout(waitForSettings, 50);
    } else {
      new CustomCursor();
    }
  };
  waitForSettings();
});

// Also initialize on Shopify's page:load event for dynamic content
document.addEventListener("page:load", () => {
  const waitForSettings = () => {
    if (typeof window.shopSettings !== "undefined") {
      new CustomCursor();
    } else {
      setTimeout(waitForSettings, 50);
    }
  };
  waitForSettings();
});
